/**
 * File Size Filtering Utility
 *
 * Filters files by size and provides rejected file information for user notification.
 * Extracts valid files for processing and identifies oversized files for warnings.
 *
 * @module utils/jobSubmission/filterOversizedFiles
 */

import { QueuedFile } from '../../types';

export interface FileFilterResult {
  validFiles: File[];
  rejectedFiles: File[];
  allRejected: boolean;
}

export interface FileFilterOptions {
  maxFileSizeMB?: number; // Default: 10MB
}

const DEFAULT_MAX_SIZE_MB = 10;

/**
 * Filters files by size limit and separates valid from rejected files
 *
 * @param fileQueue - Array of queued files to filter
 * @param options - Configuration options (max file size)
 * @returns Object containing valid files, rejected files, and rejection status
 *
 * @example
 * ```typescript
 * const result = filterOversizedFiles(state.fileQueue);
 * if (result.allRejected) {
 *   setError('All files exceed size limit');
 *   return;
 * }
 * if (result.rejectedFiles.length > 0) {
 *   showWarningToast(`${result.rejectedFiles.length} files rejected`);
 * }
 * // Proceed with result.validFiles
 * ```
 */
export function filterOversizedFiles(
  fileQueue: QueuedFile[],
  options: FileFilterOptions = {}
): FileFilterResult {
  const maxSizeMB = options.maxFileSizeMB ?? DEFAULT_MAX_SIZE_MB;
  const MAX_FILE_SIZE = maxSizeMB * 1024 * 1024; // Convert to bytes

  const files = fileQueue.map(queuedFile => queuedFile.file);
  const validFiles: File[] = [];
  const rejectedFiles: File[] = [];

  // Separate files by size
  files.forEach(file => {
    if (file.size > MAX_FILE_SIZE) {
      rejectedFiles.push(file);
    } else {
      validFiles.push(file);
    }
  });

  return {
    validFiles,
    rejectedFiles,
    allRejected: validFiles.length === 0
  };
}

/**
 * Formats rejected file information for user-friendly display
 *
 * @param rejectedFiles - Array of files that exceeded size limit
 * @returns Formatted string with file names and sizes
 *
 * @example
 * ```typescript
 * const message = formatRejectedFilesMessage(rejectedFiles);
 * // Returns: "file1.jpg (15.2 MB), file2.png (12.8 MB)"
 * ```
 */
export function formatRejectedFilesMessage(rejectedFiles: File[]): string {
  return rejectedFiles
    .map(f => `${f.name} (${(f.size / (1024 * 1024)).toFixed(2)} MB)`)
    .join(', ');
}

/**
 * Gets appropriate error message based on rejection status
 *
 * @param result - File filter result
 * @param maxSizeMB - Maximum allowed file size in MB
 * @returns User-friendly error message
 */
export function getRejectionErrorMessage(
  result: FileFilterResult,
  maxSizeMB: number = DEFAULT_MAX_SIZE_MB
): string | null {
  if (!result.allRejected) {
    return null;
  }

  const fileCount = result.rejectedFiles.length;
  return `All ${fileCount} file(s) exceed the ${maxSizeMB}MB size limit. Please select smaller files.`;
}
